/**
 * Виджет «Тех. операции» (карточка поля v3, вкладка «Обзор», PR-B).
 *
 * 3 последних операции + «все →» (открывает подэкран со всеми). Строка:
 * дата · название · подзаголовок · чип план/факт. Данные —
 * GET /fields/{id}/operations (operations.py), новые сверху (backend ORDER BY).
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Plus, Tractor } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OperationFormDialog } from '@/components/operations/OperationFormDialog';
import {
  useCompanyOpTypes,
  useCreateOperation,
} from '@/components/operations/operationsApi';
import { PERM, useCan } from '@/lib/auth/usePermissions';
import { useFieldOperations } from './widgetApi';
import {
  latestN,
  operationChip,
  operationDate,
  operationSubtitle,
  formatFullDate,
  type OpTone,
} from './widgetLogic';

const CHIP_CLASS: Record<OpTone, string> = {
  fact: 'bg-primary/15 text-primary hover:bg-primary/15',
  plan: 'bg-accent-brand/15 text-accent-brand hover:bg-accent-brand/15',
  cancelled: 'bg-muted text-muted-foreground hover:bg-muted',
};

export function OperationsWidget({
  fieldId,
  onOpenAll,
}: {
  fieldId: string;
  onOpenAll: () => void;
}) {
  const { data, isLoading, isError } = useFieldOperations(fieldId);
  const rows = latestN(data, 3);

  // AK-526: завести операцию можно прямо из карточки поля — поле уже в
  // контексте, выбирать его повторно пользователю незачем.
  const canCreate = useCan(PERM.operationWrite, 'c');
  const [createOpen, setCreateOpen] = useState(false);
  const { data: opTypes } = useCompanyOpTypes();
  const createOperation = useCreateOperation(fieldId);

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="flex items-center gap-2 leading-6">
            <Tractor className="h-4 w-4 text-muted-foreground" />
            Тех. операции
          </CardTitle>
          {(data?.length ?? 0) > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-8 px-2 text-primary"
              onClick={onOpenAll}
            >
              все
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
          {canCreate && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Добавить операцию"
              className={(data?.length ?? 0) > 0 ? 'h-8 px-2' : 'ml-auto h-8 px-2'}
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertDescription>Не удалось загрузить операции.</AlertDescription>
          </Alert>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Операций по полю пока нет.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((op) => {
              const chip = operationChip(op);
              const d = operationDate(op);
              return (
                <li key={op.id} className="flex items-center gap-3 py-2">
                  {/* AK-736: полная дата dd.MM.yyyy — «16.04» без года
                      неоднозначна на карточке, где встречаются операции
                      разных сезонов (UAT founder 21.08). w-16 вместо
                      прежних w-11 — «15.07.2026» шире «15.07». */}
                  <span className="w-16 shrink-0 text-xs tabular-nums text-muted-foreground">
                    {formatFullDate(d)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/operations/${op.id}` as string}
                      className="block truncate text-sm font-medium hover:text-primary"
                    >
                      {op.name}
                    </Link>
                    <div className="truncate text-xs text-muted-foreground">
                      {operationSubtitle(op)}
                    </div>
                  </div>
                  <Badge variant="secondary" className={`${CHIP_CLASS[chip.tone]} shrink-0`}>
                    {chip.label}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <OperationFormDialog
        open={createOpen}
        operation={null}
        opTypes={opTypes ?? []}
        fixedFieldId={fieldId}
        saving={createOperation.isPending}
        error={createOperation.error?.message ?? null}
        onSubmit={(body) =>
          createOperation.mutate(
            { ...body, field_id: fieldId },
            { onSuccess: () => setCreateOpen(false) },
          )
        }
        onClose={() => setCreateOpen(false)}
      />
    </Card>
  );
}

export default OperationsWidget;
